import {
  QueryTicketDto,
  QueryTicketMessageDto,
  QueryTicketMessageAttachmentDto,
  QueryUser,
} from '@restore/queries/queries.types';
import { FC, Fragment, useMemo, type ReactNode } from 'react';
import { FileIcon } from '@dynamika-shared/ui/icons';
import { twMerge } from '@dynamika-shared/utils/twMerge';
import { format } from 'date-fns';
import { Initials } from '@components/Queries/QueryPage/components/Initials';
import { useAppSelector } from '@hooks/redux';
import { selectAuthView } from '@restore/auth/auth.slice';
import {
  findQueryParticipant,
  formatParticipantInitials,
  formatParticipantName,
} from '@components/Queries/queryParticipants';

const formatTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) {
    return 'N/A';
  }
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const timeStr = format(date, 'HH:mm');

  if (diffDays === 0) {
    return `Today ${timeStr}`;
  } else if (diffDays === 1) {
    return `Yesterday ${timeStr}`;
  } else {
    return `${format(date, 'dd-LLL-yyyy')} ${timeStr}`;
  }
};

// eslint-disable-next-line react-refresh/only-export-components
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toPrecision(3)) + ' ' + sizes[i];
};

const renderAttachments = (
  attachments: QueryTicketMessageAttachmentDto[],
  isOwn: boolean
): ReactNode | null => {
  if (attachments.length === 0) return null;

  return (
    <div className='flex flex-col gap-1'>
      {attachments?.map(attachment => (
        <div
          key={attachment.id}
          className='flex items-start gap-2 py-1'
        >
          <FileIcon
            className={twMerge(
              'w-[20px] h-[20px]',
              isOwn ? 'text-white' : 'text-pastelBlue'
            )}
          />
          <div className='flex flex-col flex-1 gap-0.5'>
            <a
              href={attachment.downloadUrl}
              download={attachment.filename}
              target="_blank"
              rel="noopener noreferrer"
              className={twMerge(
                'w-full underline break-all hover:no-underline',
                isOwn
                  ? 'text-white/95 hover:text-white'
                  : 'text-pastelBlue hover:text-pastelBlue underline-offset-2'
              )}
            >
              {attachment.filename}
            </a>
            <span
              className={twMerge(
                'text-[1424]',
                isOwn ? 'text-white' : 'text-spanishGray'
              )}
            >
              {formatFileSize(attachment.size)}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
};

const renderMessage = (
  isOwn: boolean,
  msg: QueryTicketMessageDto,
  sender?: QueryUser
) => {
  const initials = formatParticipantInitials(sender);
  const fullName = formatParticipantName(sender);

  const time = formatTime(msg.created);

  const messageBubbleClass = twMerge(
    'text-1824 font-normal px-2 py-[10px] mt-0.5 rounded-[8px]',
    isOwn ? 'text-white bg-dodgerBlue' : 'w-full bg-white'
  );

  const timeClass = twMerge(
    'text-spanishGray text-1216 font-medium',
    isOwn && 'text-end'
  );

  const timeElement = isOwn ? (
    <div className={timeClass}>{time}</div>
  ) : (
    <div className='flex gap-[10px] items-center'>
      <div className={'text-dimGray text-1216 font-medium'}>{fullName}</div>
      <div className={timeClass}>{time}</div>
    </div>
  );

  return (
    <div
      className={twMerge(
        'mt-2 max-w-[760px]',
        isOwn ? 'self-end min-w-0' : 'flex min-w-[63%]'
      )}
    >
      {!isOwn && <Initials initials={initials} className='mt-2.5' />}
      <div className={isOwn ? 'flex flex-col items-end' : ''}>
        {timeElement}
        <div className={messageBubbleClass}>
          {msg.message.trim() && (
            <p className={msg.attachments.length > 0 ? 'mb-2' : ''}>{msg.message}</p>
          )}
          {renderAttachments(msg.attachments, isOwn)}
        </div>
      </div>
    </div>
  );
};

type QueryChatProps = {
  query: QueryTicketDto | null;
};

export const QueryChat: FC<QueryChatProps> = ({ query }) => {
  const { uuid } = useAppSelector(selectAuthView);
  const currentUuid = uuid;
  const isOwnMessage = (msg: QueryTicketMessageDto) =>
    msg.actedBy === currentUuid;

  const processedMessages = useMemo(() => {
    const messages = query?.messages || [];
    return messages
      .filter(msg => msg.message.trim() || msg.attachments.length > 0)
      .sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
  }, [query?.messages]);

  if (!query) return null;

  return (
    <div className='h-full flex flex-col overflow-hidden min-h-0 -me-1'>
      <div className='flex flex-col flex-1 overflow-y-auto pe-1'>
        {processedMessages.map(msg => (
          <Fragment key={msg.id}>
            {renderMessage(
              isOwnMessage(msg),
              msg,
              findQueryParticipant(query, msg.actedBy)
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
};
